import { CommonModule } from '@angular/common';
import { Component, inject, input, output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import {
  QaiButtonComponent,
  QaiDialogComponent,
  QaiFieldComponent,
  QaiInputComponent,
  QaiFloatingSelectComponent,
} from '@qai/ui';
import { ASSIGNMENT_NAME_ALREADY_EXISTS_STATUS } from 'src/app/services/self-serve/assignments.service';
import { SIMULATION_NAME_ALREADY_EXISTS_STATUS, AuthorService } from 'src/app/services/self-serve/author.service';
import { EntityActionType, EntityType } from 'src/app/utils/enums/self-serve/assignment-type.enum';
import { IModule } from 'src/app/interfaces/self-serve/responses/imodule';
import { IConfirmActionPayload } from 'src/app/interfaces/self-serve/requests/iconfirm-action-payload';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { LeaveConfirmationModalComponent } from '../leave-confirmation-modal/leave-confirmation-modal.component';

@Component({
  selector: 'action-modal',
  standalone: true,
  templateUrl: './action-modal.component.html',
  styleUrls: ['./action-modal.component.scss'],
  imports: [
    FormsModule,
    CommonModule,
    QaiFieldComponent,
    QaiDialogComponent,
    QaiButtonComponent,
    QaiInputComponent,
    QaiFloatingSelectComponent,
    TranslocoModule,
    LeaveConfirmationModalComponent,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class ActionModalComponent {
  private translocoService = inject(TranslocoService);

  @ViewChild('actionDialog') qaiDialogRef?: QaiDialogComponent;

  open = input(false);
  entityType = input<EntityType>(EntityType.SIMULATION);
  entityActionType = input<EntityActionType>(EntityActionType.CREATE);
  originalName = input('');
  networkRequestInProgress = input(false);
  errorStatus = input<number>();

  confirmAction = output<IConfirmActionPayload>();
  closeModal = output<void>();

  inputName = '';
  showError = false;
  duplicateError = false;
  moduleOptions: { label: string; value: string }[] = [];
  selectedModuleUuid: string = '';
  isLoadingModuleOptions: boolean = false;
  showLeaveConfirmation = false;
  isLeaving = false;

  EntityActionType = EntityActionType;
  EntityType = EntityType;

  constructor(private authorService: AuthorService) {}

  loadProducts(): void {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    if (!accountUuid) {
      console.error('No account UUID found for loading module options');
      this.moduleOptions = [];
      return;
    }

    this.isLoadingModuleOptions = true;
    this.authorService.getModuleList(accountUuid).subscribe({
      next: (modules: IModule[]) => {
        this.moduleOptions = modules.map((module) => ({
          label: module.name,
          value: module.uuid,
        }));
        this.isLoadingModuleOptions = false;
      },
      error: (err: any) => {
        console.error('Failed to load module options:', err);
        this.moduleOptions = [];
        this.isLoadingModuleOptions = false;
      },
    });
  }

  onAddNewProduct(moduleName: string): void {
    if (!moduleName || moduleName.trim() === '') {
      return;
    }

    this.isLoadingModuleOptions = true;
    this.authorService.createModule(moduleName.trim()).subscribe({
      next: (newModule: IModule) => {
        this.moduleOptions.push({
          label: newModule.name,
          value: newModule.uuid,
        });
        this.selectedModuleUuid = newModule.uuid;
        this.isLoadingModuleOptions = false;
      },
      error: (err: any) => {
        console.error('Failed to create new module:', err);
        this.isLoadingModuleOptions = false;
      },
    });
  }

  onModuleSelectionChange(value: string | undefined): void {
    if (value) {
      this.selectedModuleUuid = value;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      const isOpenNow = changes['open']?.currentValue;

      if (isOpenNow) {
        this.qaiDialogRef?.showModal();

        if (
          this.entityActionType() === EntityActionType.DUPLICATE &&
          this.originalName()
        ) {
          this.inputName = this.translocoService.translate('simulationBuilder.COPY_OF_PREFIX', {
            name: this.originalName(),
          });
          this.showError = false;
          this.duplicateError = false;
        } else {
          this.inputName = '';
          this.showError = this.entityActionType() === EntityActionType.CREATE;
          this.duplicateError = false;
        }

        this.showLeaveConfirmation = false;
        this.isLeaving = false;

        if (this.entityType() === EntityType.SIMULATION) {
          this.selectedModuleUuid = '';
          this.loadProducts();
        }
      } else {
        this.qaiDialogRef?.close();
      }
    }

    if (changes['errorStatus']) {
      this.handleErrorStatusChange(changes['errorStatus'].currentValue);
    }
  }

  private handleErrorStatusChange(status?: number): void {
    if (
      (status === ASSIGNMENT_NAME_ALREADY_EXISTS_STATUS &&
      this.entityActionType() === EntityActionType.DUPLICATE) || (status === SIMULATION_NAME_ALREADY_EXISTS_STATUS)
    ) {
      this.duplicateError = true;
      this.showError = false;
    } else if (status === ASSIGNMENT_NAME_ALREADY_EXISTS_STATUS) {
      this.showError = true;
      this.duplicateError = false;
    }
  }

  onInputChange(): void {
    this.showError = false;
    this.duplicateError = false;
  }

  handleConfirmClick() {
    if (this.networkRequestInProgress()) {
      return;
    }

    if (this.inputName.trim()) {
      this.showError = false;
      this.duplicateError = false;
      const payload: IConfirmActionPayload = {
        name: this.inputName.trim(),
      };
      if (this.entityType() === EntityType.SIMULATION && this.selectedModuleUuid) {
        payload.moduleUuid = this.selectedModuleUuid;
      }
      this.confirmAction.emit(payload);
    } else {
      this.showError = true;
      this.duplicateError = false;
    }
  }

  handleCloseClick() {
    this.resetFormState();
    this.closeModal.emit();
  }

  onLeaveCancelled(): void {
    this.showLeaveConfirmation = false;
    this.qaiDialogRef?.showModal();
  }

  onLeaveConfirmed(): void {
    this.showLeaveConfirmation = false;
    this.resetFormState();
    this.closeModal.emit();
  }

  protected resetFormState(): void {
    this.inputName = '';
    this.selectedModuleUuid = '';
    this.showError = false;
    this.duplicateError = false;
  }

  isConfirmDisabled(): boolean {
    if (this.networkRequestInProgress() || !this.inputName.trim()) {
      return true;
    }

    if (this.entityType() === EntityType.SIMULATION && !this.selectedModuleUuid) {
      return true;
    }

    return false;
  }

  confirmButtonLabel(): string {
    const action = this.translocoService.translate(
      this.entityActionType() === EntityActionType.DUPLICATE ? 'simulationBuilder.ACTION_DUPLICATE' : 'simulationBuilder.ACTION_CREATE'
    );
    const type = this.translocoService.translate(
      this.entityType() === EntityType.ASSIGNMENT ? 'simulationBuilder.ENTITY_TYPE_ASSIGNMENT_LOWER' : 'simulationBuilder.ENTITY_TYPE_SIMULATION_LOWER'
    );
    return this.translocoService.translate('simulationBuilder.CONFIRM_BUTTON_LABEL', { action, type });
  }

  get shouldShowError(): boolean {
    return this.showError || this.duplicateError;
  }

  get errorMessage(): string {
    if (this.duplicateError) {
      return this.translocoService.translate(
        this.entityType() === EntityType.ASSIGNMENT
          ? 'simulationBuilder.DUPLICATE_NAME_ERROR_ASSIGNMENT'
          : 'simulationBuilder.DUPLICATE_NAME_ERROR_SIMULATION'
      );
    }
    if (this.showError) {
      return this.translocoService.translate('simulationBuilder.NAME_REQUIRED_ERROR');
    }
    return '';
  }
}
